import { useContext, useEffect, useState } from "react";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import { AuthContext } from "../../context/auth-context";
import { ClinicaContext } from "../../context/clinica-context";
import { useHttpClient } from "../../hooks/http-hook";
import "./BadgeUnidade.scss";

export const TODAS_UNIDADES = "todas";

/**
 * Escolhe de que unidade são os dados do ecrã — e faz o servidor devolver
 * apenas esses.
 *
 * NÃO é um filtro. Um filtro corta o que já veio; isto muda o que se pede.
 * A distinção não é académica: nos ecrãs de dinheiro a unidade é o CONTEXTO,
 * não um recorte. Quem fecha a caixa ao fim do dia quer o total daquele balcão,
 * e um consolidado das duas unidades, mesmo que rotulado, é um número errado à
 * espera de ser conferido contra o dinheiro físico.
 *
 * Por isso vive no topo do ecrã e não dentro do modal de filtros. Escolher
 * dispara novo pedido; o que está no ecrã é sempre o que está escolhido, e
 * acaba a classe de bug em que o filtro dizia uma coisa e a lista mostrava
 * outra.
 *
 * Substituiu o `RotuloUnidadesMostradas`, que dizia o mesmo mas não deixava
 * mudar nada. Gémeo do `SeletorUnidadeDados` da v2 — mantê-los iguais.
 *
 * ⚠️ É UMA unidade, ou todas — não é multiselect. Com duas unidades cobre tudo
 * (uma, a outra, ou as duas) e evita os dois cliques e o «Aplicar» que um
 * multiselect obrigaria. Com TRÊS ou mais deixa de cobrir: não dá para pedir
 * «estas duas, mas não a terceira». O servidor já aceita lista
 * (`estabelecimentos`, no plural), por isso a mudança seria só aqui e no
 * `paramsUnidadeDados`. Ver `planos/por-fazer.md`.
 *
 * @param {string} valor  id da unidade, ou `TODAS_UNIDADES`
 * @param {(v: string) => void} onChange
 */
const SeletorUnidadeDados = ({ valor, onChange }) => {
  const auth = useContext(AuthContext);
  const { clinica } = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();

  const [unidades, setUnidades] = useState([]);
  const [ancora, setAncora] = useState(null);

  const clinicaId = clinica?._id;
  const multi = !!clinica?.multiEstabelecimento;

  /* Sem unidade na sessão não há contexto nenhum a mostrar nem a escolher.
     É o estado ANTES da migração — e também o das clínicas de unidade única,
     se alguma tiver o interruptor ligado por engano. Sem esta verificação a
     pastilha aparecia a dizer «Unidade atual», que não quer dizer nada.
     Encontrado nos testes da fase 1, a 2026-08-19. */
  const semUnidadeNaSessao = !auth?.estabelecimento?._id;

  useEffect(() => {
    if (!multi || !clinicaId) return;

    let vivo = true;
    const buscar = async () => {
      try {
        const resposta = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/estabelecimentos/clinica/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        if (vivo) setUnidades(resposta.estabelecimentos || []);
      } catch (err) {
        console.error("Erro ao buscar unidades:", err);
      }
    };
    buscar();
    return () => {
      vivo = false;
    };
  }, [multi, clinicaId, auth.token, sendRequest]);

  if (!multi || semUnidadeNaSessao) return null;

  /* Uma pessoa com acesso a uma só unidade não tem escolha a fazer — e por
     isso não se lhe mostra nada.

     Mostrava-se a pastilha na mesma, «para dizer onde está». Era conversa a
     mais: quem só entra numa unidade já sabe em qual está, e a pastilha só lhe
     ocupava a linha dos controlos. Na lista de atendimentos da secretária
     chegou a empurrar o «Ver Compromissos» para fora do ecrã. */
  const podeEscolher = unidades.length > 1;

  const escolhida =
    valor === TODAS_UNIDADES
      ? null
      : unidades.find((u) => u._id === valor) || auth.estabelecimento;

  const etiqueta =
    valor === TODAS_UNIDADES
      ? "Todas as unidades"
      : escolhida?.nome || escolhida?.codigo || "Unidade atual";

  const escolher = (novo) => {
    setAncora(null);
    if (novo !== valor) onChange(novo);
  };

  const pilula = (
    <>
      <StorefrontIcon className="rotulo-unidades__icone" />
      <span>
        A mostrar: <strong>{etiqueta}</strong>
      </span>
      {podeEscolher && (
        <ExpandMoreIcon className="rotulo-unidades__seta" fontSize="inherit" />
      )}
    </>
  );

  if (!podeEscolher) return null;

  return (
    <>
      <div
        className="rotulo-unidades rotulo-unidades--clicavel"
        onClick={(e) => setAncora(e.currentTarget)}
        title="Escolher de que unidade são os dados mostrados"
      >
        {pilula}
      </div>

      <Menu
        anchorEl={ancora}
        open={!!ancora}
        onClose={() => setAncora(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {unidades.map((u) => (
          <MenuItem key={u._id} onClick={() => escolher(u._id)}>
            <span style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontWeight: 500 }}>
                {u.codigo} — {u.nome}
              </span>
              {(u.cidade || u.endereco) && (
                <span style={{ fontSize: 12, opacity: 0.7 }}>
                  {u.cidade || u.endereco}
                </span>
              )}
            </span>
            {u._id === valor && (
              <CheckIcon fontSize="small" style={{ marginLeft: "auto" }} />
            )}
          </MenuItem>
        ))}

        <MenuItem onClick={() => escolher(TODAS_UNIDADES)}>
          <span style={{ fontWeight: 500 }}>Todas as unidades</span>
          {valor === TODAS_UNIDADES && (
            <CheckIcon fontSize="small" style={{ marginLeft: "auto" }} />
          )}
        </MenuItem>
      </Menu>
    </>
  );
};

export default SeletorUnidadeDados;
